/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Normalize FastAPI / axios error payloads into a string safe for snackbars.
 * MODULE_NOT_ENTITLED and similar 403s return `detail` as an object.
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422: [{ type, loc, msg, input }, ...] — never put this array in JSX.
    const msgs = detail
      .map((item) => (typeof item?.msg === 'string' ? item.msg : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  } else if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message;
    }
    if (typeof detail.msg === 'string' && detail.msg.trim()) {
      return detail.msg;
    }
  }
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return error?.message || fallback;
}

export function isModuleNotEntitledError(error) {
  const detail = error?.response?.data?.detail;
  return (
    error?.response?.status === 403 &&
    detail &&
    typeof detail === 'object' &&
    detail.error_code === 'MODULE_NOT_ENTITLED'
  );
}

/** WhatsApp / Call Center seat missing for this user (403 SEAT_NOT_ASSIGNED). */
export function isSeatNotAssignedError(error) {
  const detail = error?.response?.data?.detail;
  return (
    error?.response?.status === 403 &&
    detail &&
    typeof detail === 'object' &&
    detail.error_code === 'SEAT_NOT_ASSIGNED'
  );
}

/**
 * Human message for seat refusals. Explains queue membership is not enough when
 * the tenant has zero purchased seats (common WhatsApp inbox blank-screen case).
 */
export function getSeatNotAssignedMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (!isSeatNotAssignedError(error)) {
    return getApiErrorMessage(error, fallback);
  }
  const base =
    (typeof detail.message === 'string' && detail.message.trim()) ||
    fallback ||
    'You do not have a license seat for this feature.';
  const total = Number(detail.seats_total);
  if (Number.isFinite(total) && total <= 0) {
    return (
      `${base} This tenant has 0 WhatsApp seats purchased — add WhatsApp seats on the ` +
      'subscription, then assign a seat to each agent. Being in a queue alone is not enough.'
    );
  }
  const remaining = Number(detail.seats_remaining);
  if (Number.isFinite(remaining) && remaining <= 0) {
    return `${base} All ${detail.seats_total} seats are already assigned.`;
  }
  return base;
}

/**
 * Tenant lifecycle lockouts: the workspace is suspended, archived or
 * deactivated at the platform level.
 *
 * Deliberately a **403** on the wire, not a 401. The 401 path in auth/Auth.js
 * runs refresh-then-signout, which is exactly what made a suspension look
 * identical to an ordinary expired session -- the user was bounced to the
 * login page with no explanation. Same detail shape as MODULE_NOT_ENTITLED
 * above, so it slots into handling that already exists.
 */
export const TENANT_LOCKOUT_CODES = Object.freeze([
  'TENANT_SUSPENDED',
  'TENANT_ARCHIVED',
  'TENANT_INACTIVE',
]);

export function isTenantLockoutError(error) {
  const detail = error?.response?.data?.detail;
  return (
    error?.response?.status === 403 &&
    detail &&
    typeof detail === 'object' &&
    TENANT_LOCKOUT_CODES.includes(detail.error_code)
  );
}

/** The bits the lockout screen renders. Null when this is not a lockout. */
export function getTenantLockout(error) {
  if (!isTenantLockoutError(error)) return null;
  const detail = error.response.data.detail;
  return {
    errorCode: detail.error_code,
    status: detail.tenant_status || null,
    message: detail.message || null,
    reason: detail.reason || null,
    suspendedAt: detail.suspended_at || null,
  };
}

/**
 * Gmail/Outlook/SMTP auth failure for the user's connected inbox.
 * Historically returned as HTTP 401 with "Token refresh failed", which the
 * CRM interceptor treated as a Pearl session expiry and signed the user out.
 * Backend now returns 502 + EMAIL_PROVIDER_AUTH_FAILED; keep string matches
 * for older responses still in flight.
 */
export function isEmailProviderAuthFailure(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (
    detail &&
    typeof detail === 'object' &&
    detail.error_code === 'EMAIL_PROVIDER_AUTH_FAILED'
  ) {
    return true;
  }
  if (status !== 401 && status !== 502) return false;
  const text = (
    typeof detail === 'string'
      ? detail
      : typeof detail?.message === 'string'
        ? detail.message
        : ''
  ).toLowerCase();
  return (
    text.includes('token refresh failed') ||
    text.includes('failed to refresh token') ||
    text.includes('smtp authentication failed') ||
    text.includes('email connection expired') ||
    text.includes('connected email inbox expired')
  );
}
